import { useEffect, useState } from "react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import EditIcon from "@mui/icons-material/Edit";
import RouteIcon from "@mui/icons-material/Route";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  CircularProgress,
  Typography,
} from "@mui/material";
import { Title, useGetOne, useNotify, usePermissions, useRedirect, useStore } from "react-admin";
import dayjs from "dayjs";
import { callApi } from "@/dataprovider/misc-apis";
import { formatEventCardDateRange, formatIsoDate } from "@/utils/date-format";
import { hasPermission } from "@/utils/permission-utils";
import { navigateToBaseRoute, useRouteId } from "@/utils/route-utility";
import type { HouseVisitEventRecord } from "../types";

type ActionCardProps = {
  title: string;
  description: string;
  icon: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
};

const ActionCard = ({ title, description, icon, onClick, disabled }: ActionCardProps) => (
  <Card variant="outlined" sx={{ height: "100%", opacity: disabled ? 0.55 : 1 }}>
    <CardActionArea onClick={onClick} disabled={disabled} sx={{ height: "100%", minHeight: 108 }}>
      <CardContent
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 1.5,
          py: 2,
          height: "100%",
          "&:last-child": { pb: 2 },
        }}
      >
        <Box sx={{ color: "primary.main", mt: 0.25 }}>{icon}</Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight={700}>
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {description}
          </Typography>
        </Box>
      </CardContent>
    </CardActionArea>
  </Card>
);

const ActiveHouseVisitDashboard = () => {
  const eventId = useRouteId();
  const notify = useNotify();
  const redirect = useRedirect();
  const { permissions } = usePermissions();
  const [, setCurrentEvent] = useStore<HouseVisitEventRecord | null>(
    "currentHouseVisitEvent",
    null
  );
  const [generating, setGenerating] = useState(false);

  const { data, isPending, error } = useGetOne(
    "houseVisitEvents",
    { id: eventId ?? "" },
    { enabled: Boolean(eventId) }
  );

  useEffect(() => {
    if (data) {
      setCurrentEvent(data as HouseVisitEventRecord);
    }
  }, [data, setCurrentEvent]);

  if (!eventId) return null;

  if (isPending) {
    return (
      <Box sx={{ p: 3, display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !data) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="text.secondary">Could not load this event.</Typography>
        <Button sx={{ mt: 1 }} onClick={() => navigateToBaseRoute("houseVisits", null)}>
          Back to events
        </Button>
      </Box>
    );
  }

  const event = data as HouseVisitEventRecord;
  const todayIso = formatIsoDate(dayjs(), { utc: true });
  const canGenerate = hasPermission(permissions, "houseVisitSlots.create");
  const canEditEvent = hasPermission(permissions, "houseVisitEvents.edit");

  const generateSlots = async () => {
    setGenerating(true);
    try {
      const res = await callApi({
        location: "houseVisitSlots",
        method: "POST",
        id: "generate",
        data: { eventId },
      });
      const body = res.data as { count?: number; rows?: unknown[] };
      const n = body.count ?? body.rows?.length ?? 0;
      notify(n > 0 ? `Generated ${n} slot(s)` : "No new slots needed", { type: "info" });
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } }; message?: string })?.response?.data
          ?.message ||
        (err as Error)?.message ||
        "Failed to generate slots";
      notify(message, { type: "warning" });
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 }, pb: 4, maxWidth: 1100, mx: "auto", width: "100%" }}>
      <Title title={event.name} />
      <Button
        variant="outlined"
        color="inherit"
        startIcon={<ArrowBackIcon />}
        onClick={() => navigateToBaseRoute("houseVisits", null)}
        sx={{ mb: 2 }}
      >
        All events
      </Button>

      <Typography
        variant="h5"
        fontWeight={700}
        sx={{ fontSize: { xs: "1.25rem", md: "1.6rem" }, letterSpacing: "-0.02em" }}
      >
        {event.name}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
        Hijri {event.hijriYear} · {formatEventCardDateRange(event.fromDate, event.toDate)}
      </Typography>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, minmax(0, 1fr))",
          },
          gap: 1.5,
        }}
      >
        <ActionCard
          title="Today's visits"
          description="Run the day's route — mark visited, not home, or skipped"
          icon={<RouteIcon />}
          onClick={() => redirect(`/houseVisitDaily?date=${todayIso}`)}
        />
        <ActionCard
          title="Plan slots"
          description="View and edit stop lists for each day"
          icon={<CalendarMonthIcon />}
          onClick={() => redirect("/houseVisitSlots")}
        />
        {canGenerate ? (
          <ActionCard
            title="Generate Mon–Sat slots"
            description={
              generating ? "Generating…" : "Create missing weekday slots for the event range"
            }
            icon={generating ? <CircularProgress size={22} /> : <AutoAwesomeIcon />}
            onClick={() => {
              void generateSlots();
            }}
            disabled={generating}
          />
        ) : null}
        {canEditEvent ? (
          <ActionCard
            title="Edit event"
            description="Name, hijri year, and date range"
            icon={<EditIcon />}
            onClick={() => redirect("edit", "houseVisitEvents", eventId)}
          />
        ) : null}
      </Box>
    </Box>
  );
};

export default ActiveHouseVisitDashboard;
